import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { createContext, HTMLAttributes, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface SlidePanel extends HTMLAttributes<HTMLDivElement> {
  open: boolean;
  onClose?: () => void;
  size?: 'small' | 'medium' | 'large';
  scorllTarget?: string;
  // TODO: Remove after refactoring infobox, smartnote, calc intergration
  supportLegacyFormWidth?: boolean;
}

const NestedContext = createContext(false);

export const SlidePanel = ({
  open,
  onClose,
  children,
  scorllTarget = 'module-layout-scroll-target',
  supportLegacyFormWidth = false,
}: SlidePanel) => {
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const isNested = useContext(NestedContext);
  const [openState, setOpenState] = useState(open);
  const openRef = useRef(open);
  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef(null);

  useEffect(() => {
    setOpenState(open);
    openRef.current = open;
  }, [open]);
  const closePanel = () => {
    setOpenState(false);
    openRef.current = false;
  };

  useEffect(() => {
    if (!scorllTarget || !openRef.current || !overlayRef.current) return;

    const handleDocumentScroll = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      if (openRef.current) {
        const target = document.querySelector(`#${scorllTarget}`);
        if (target) {
          const deltaY = (event as WheelEvent).deltaY;
          target.scrollBy(0, deltaY);
        }
      }
    };

    overlayRef.current.addEventListener('wheel', handleDocumentScroll, { passive: false });

    return () => {
      overlayRef.current?.removeEventListener('wheel', handleDocumentScroll);
    };
  }, [openRef.current, overlayRef.current]);

  // If the panel is opened inside a floating UI component, the UI breaks because the panel is dependent on its parent floating component.
  // To prevent this, the panel is rendered in a portal to decouple it from the parent and avoid layout issues.
  return createPortal(
    <NestedContext.Provider value={true}>
      {openState && (
        <div
          ref={overlayRef}
          className={clsx('fixed inset-0 z-20', !isNested && 'bg-gray-bg-bright')}
          // onClick={() => setCloseModalOpen(true)}
          // onScroll={handleScroll}
        />
      )}
      <AnimatePresence>
        {openState && (
          <>
            <motion.div
              ref={panelRef}
              className={clsx(
                'fixed top-0 h-screen bg-white pt-[51px]',
                supportLegacyFormWidth ? 'w-[1210px]' : 'w-1/2 min-w-[700px] max-w-[856px]',
                isNested ? 'z-40' : 'z-30'
              )}
              initial={{ right: '-100%', opacity: 0 }}
              animate={{
                right: 0,
                opacity: 1,
                transition: { ease: 'easeInOut' },
              }}
              exit={{
                right: '-100%',
                opacity: 0,
                transition: { ease: 'easeInOut' },
              }}
            >
              <div className='h-full w-full overflow-y-auto shadow-08'>{children}</div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </NestedContext.Provider>,
    document.body
  );
};
